import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { getActiveUser, logoutUser } from './authService';

const Header = ({ onOpenAuth }) => {
  const history = useHistory();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);

  const updateHeaderState = () => {
    setCurrentUser(getActiveUser());
  };

  useEffect(() => {
    updateHeaderState();
    window.addEventListener('auth_state_changed', updateHeaderState);
    return () => {
      window.removeEventListener('auth_state_changed', updateHeaderState);
    };
  }, []);

  const handleSignOut = () => {
    logoutUser();
  };

  const handleStartBuildClick = (e) => {
    e.preventDefault();
    const scrollToAndFocus = () => {
      const el = document.getElementById('intake');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => {
          const inputEl = document.getElementById('intake-project-title') || document.querySelector('#intake input');
          if (inputEl) {
            inputEl.focus();
            inputEl.style.boxShadow = '0 0 0 3px rgba(56, 189, 248, 0.45)';
            setTimeout(() => {
              inputEl.style.boxShadow = 'none';
            }, 1800);
          }
        }, 500);
      }
    };

    if (window.location.pathname === '/') {
      scrollToAndFocus();
    } else {
      history.push('/#intake');
      setTimeout(scrollToAndFocus, 150);
    }
  };

  return (
    <Navbar 
      className="sticky-top" 
      expand="lg"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)',
        padding: '0.65rem 0',
        minHeight: '60px'
      }}
    >
      <Container className="container-custom">
        {/* Slender Minimalist Brand Identity */}
        <Navbar.Brand as={Link} to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: 0 }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: '#1d4ed8',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 900,
            fontSize: '1.15rem',
            boxShadow: '0 4px 10px rgba(29, 78, 216, 0.25)'
          }}>
            G
          </div>
          <div>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1.12rem', letterSpacing: '-0.025em', color: '#0f172a', lineHeight: 1.1 }}>
              Guru&apos;s Workshop<span style={{ color: '#1d4ed8' }}>.</span>
            </div>
            <div style={{ fontSize: '0.62rem', letterSpacing: '0.06em', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
              Custom Software &bull; Cloud Architecture
            </div>
          </div>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="workshop-nav" style={{ border: 'none', padding: '0.3rem' }} />

        <Navbar.Collapse id="workshop-nav">
          <Nav className="ml-auto" style={{ alignItems: 'center', gap: '0.6rem' }}>
            <Nav.Link 
              as={Link} 
              to="/" 
              style={{
                color: location.pathname === '/' ? '#1d4ed8' : '#475569',
                fontWeight: location.pathname === '/' ? 700 : 500,
                fontSize: '0.88rem',
                padding: '0.4rem 0.6rem',
                borderBottom: location.pathname === '/' ? '2px solid #1d4ed8' : '2px solid transparent',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#1d4ed8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = location.pathname === '/' ? '#1d4ed8' : '#475569'; }}
            >
              Overview
            </Nav.Link>

            <Nav.Link 
              href="/#ecosystem" 
              style={{ color: '#475569', fontWeight: 600, fontSize: '0.88rem', padding: '0.4rem 0.6rem', transition: 'color 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#1d4ed8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; }}
            >
              Shipped Systems
            </Nav.Link>

            <Nav.Link 
              href="/#services" 
              style={{ color: '#475569', fontWeight: 500, fontSize: '0.88rem', padding: '0.4rem 0.6rem', transition: 'color 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#1d4ed8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; }}
            >
              Capabilities
            </Nav.Link>

            <Nav.Link 
              as={Link} 
              to="/project" 
              style={{
                color: location.pathname.startsWith('/project') ? '#1d4ed8' : '#475569',
                fontWeight: location.pathname.startsWith('/project') ? 700 : 500,
                fontSize: '0.88rem',
                padding: '0.4rem 0.6rem',
                borderBottom: location.pathname.startsWith('/project') ? '2px solid #1d4ed8' : '2px solid transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#1d4ed8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = location.pathname.startsWith('/project') ? '#1d4ed8' : '#475569'; }}
            >
              <span>📋</span> Track Project
            </Nav.Link>

            {/* Subtle Divider */}
            <div className="nav-divider" style={{ width: '1px', height: '16px', backgroundColor: '#e2e8f0', margin: '0 0.3rem' }}></div>

            {/* Authentication / User Identity Status */}
            {currentUser ? (
              <div className="nav-user-profile" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                {currentUser.role === 'admin' ? (
                  <Link
                    to="/admin"
                    style={{
                      backgroundColor: location.pathname.startsWith('/admin') ? '#eff6ff' : '#f8fafc',
                      border: location.pathname.startsWith('/admin') ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                      color: location.pathname.startsWith('/admin') ? '#1d4ed8' : '#475569',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      padding: '0.35rem 0.75rem',
                      borderRadius: '999px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <span>🔐</span> Workshop Admin
                  </Link>
                ) : (
                  <Link
                    to="/dashboard"
                    style={{
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#0f172a',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      padding: '0.35rem 0.8rem',
                      borderRadius: '999px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <span>👤</span> {currentUser.name ? currentUser.name.split(' ')[0] : 'Client'}
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    padding: '0.2rem',
                    whiteSpace: 'nowrap'
                  }}
                  title="Sign Out"
                >
                  Log out
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="nav-auth-btn"
                onClick={() => { if (onOpenAuth) onOpenAuth('login'); }}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#334155',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  padding: '0.38rem 0.85rem',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1d4ed8'; e.currentTarget.style.color = '#1d4ed8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
              >
                Sign In
              </button>
            )}

            {/* Clear Primary Action Button: "Start a Build →" */}
            <a 
              href="/#intake"
              onClick={handleStartBuildClick}
              title="Start a Build: Instant interactive project scope, milestone budget, and SLA intake"
              className="btn-primary-blue nav-cta-btn"
              style={{
                backgroundColor: '#1d4ed8',
                color: '#ffffff',
                padding: '0.45rem 1.15rem',
                fontSize: '0.84rem',
                fontWeight: 700,
                borderRadius: '9999px',
                textDecoration: 'none',
                marginLeft: '0.5rem',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                boxShadow: '0 4px 14px rgba(29, 78, 216, 0.25)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                lineHeight: 1
              }}
            >
              Start a Build →
            </a>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;
